/**
 * Category Model Class
 * Handles the data structure for categories and subcategories in the application
 */
class Category {
  constructor(data = {}) {
    this.id = data.id || null;
    this.name = data.name || '';
    this.description = data.description || '';
    this.slug = data.slug || '';
    this.image = data.image || '';
    this.subcategories = data.subcategories || [];
    this.displayOrder = data.displayOrder || 0;
    this.active = data.active !== undefined ? data.active : true;
    this.createdAt = data.createdAt || null;
    this.updatedAt = data.updatedAt || null;
  }

  /**
   * Creates a Category instance from Firestore data
   * @param {string} id - Document ID
   * @param {Object} data - Firestore document data
   * @returns {Category} - Category instance
   */
  static fromFirestore(id, data) {
    return new Category({
      id,
      ...data,
      // Convert Firestore Timestamp to JS Date if needed
      createdAt: data.createdAt ? new Date(data.createdAt.toDate()) : null,
      updatedAt: data.updatedAt ? new Date(data.updatedAt.toDate()) : null
    });
  }

  /**
   * Converts the Category instance to a Firestore-compatible object
   * @returns {Object} - Object ready for Firestore
   */
  toFirestore() {
    // Remove id and null values before saving
    const { id, ...data } = this;
    
    // Create a clean object with non-null values
    const firestoreData = {};
    
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        // Format subcategories if present
        if (key === 'subcategories' && Array.isArray(value)) {
          firestoreData[key] = value.map((subcategory) => {
            // If subcategory is already a string, just return it
            if (typeof subcategory === 'string') {
              return subcategory;
            }
            
            // Preserve the original ID exactly as it is - critical for references
            return {
              // Ensure ID is preserved exactly as is
              id: subcategory.id,
              name: subcategory.name || '',
              description: subcategory.description || '',
              ...(subcategory.price !== undefined ? { price: parseFloat(subcategory.price) || 0 } : {}),
              ...(subcategory.active !== undefined ? { active: subcategory.active } : { active: true })
            };
          });
        } else {
          firestoreData[key] = value;
        }
      }
    });
    
    return firestoreData;
  }

  /**
   * Add a subcategory to this category
   * @param {Object} subcategory - Subcategory data
   * @returns {Category} - Updated category instance
   */
  addSubcategory(subcategory) {
    // Generate an ID if not provided
    if (!subcategory.id) {
      subcategory.id = Math.random().toString(36).substr(2, 9);
    }
    
    this.subcategories.push(subcategory);
    return this;
  }

  /**
   * Remove a subcategory by ID
   * @param {string} subcategoryId - ID of subcategory to remove
   * @returns {Category} - Updated category instance
   */
  removeSubcategory(subcategoryId) {
    this.subcategories = this.subcategories.filter(sub => sub.id !== subcategoryId);
    return this;
  }

  /**
   * Update a subcategory by ID
   * @param {string} subcategoryId - ID of subcategory to update
   * @param {Object} updates - Fields to update
   * @returns {Category} - Updated category instance
   */
  updateSubcategory(subcategoryId, updates) {
    this.subcategories = this.subcategories.map(sub => {
      if (sub.id === subcategoryId) {
        return { ...sub, ...updates };
      }
      return sub;
    });
    return this;
  }

  /**
   * Generate a slug from the category name
   * @returns {string} - URL-friendly slug
   */
  generateSlug() {
    if (!this.name) return '';
    
    const slug = this.name
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
      
    this.slug = slug;
    return slug;
  }

  /**
   * Check if category has subcategories
   * @returns {boolean}
   */
  hasSubcategories() {
    return this.subcategories && this.subcategories.length > 0;
  }

  /**
   * Get a specific subcategory by ID
   * @param {string} subcategoryId - ID of subcategory to find
   * @returns {Object|null} - Subcategory object or null if not found
   */
  getSubcategory(subcategoryId) {
    return this.subcategories.find(sub => sub.id === subcategoryId) || null;
  }
}

export default Category; 